import { Badge, Card, Text, Title } from '@tremor/react';
import type { ServiceOrder } from '../../api/types';
import { statusKey, type StatusKey } from './Resumen';
import { t } from '../../lib/i18n';
import { localYMD } from '../../lib/format';

export interface WoCard {
  sid: number; tnum: number; anum_name: string; vehicle: string; promise_date: string;
  promise_time: number; total: number; tech: string; jobs: number; status: StatusKey;
}
const COLUMNS: StatusKey[] = ['A', 'S', 'W', 'H', 'bko', 'C'];

export function groupByStatus(orders: ServiceOrder[]): Record<StatusKey, WoCard[]> {
  const byWo = new Map<string, WoCard & { _parts: number }>();
  for (const o of orders) {
    const key = `${o.sid}-${o.tnum}`;
    const cur = byWo.get(key) ?? {
      sid: o.sid, tnum: o.tnum, anum_name: o.anum_name || String(o.anum),
      vehicle: [o.equip_desc, o.equip_plate].filter(Boolean).join(' · '),
      promise_date: o.promise_date, promise_time: o.promise_time, total: 0,
      tech: o.header_tech_name || o.tech_name || o.tech || '—', jobs: 0, status: statusKey(o), _parts: o.parts_sell_mxn,
    };
    cur.total += o.price_mxn; cur.jobs += 1; byWo.set(key, cur);
  }
  const out: Record<StatusKey, WoCard[]> = { A: [], S: [], W: [], H: [], C: [], bko: [] };
  for (const c of byWo.values()) { c.total += c._parts; out[c.status].push(c); }
  return out;
}

export function isOverdue(c: { promise_date: string; status: StatusKey }, today: Date): boolean {
  if (!c.promise_date || c.status === 'C') return false;
  return c.promise_date < localYMD(today);
}

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });

export default function Tablero({ orders, onOpen }: { orders: ServiceOrder[]; onOpen: (sid: number, tnum: number) => void }) {
  const g = groupByStatus(orders);
  const today = new Date();
  const c = t.service.cockpit;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
      {COLUMNS.map((col) => (
        <div key={col} className="space-y-2">
          <Title className="text-sm">{c.status[col]} <span className="text-tremor-content">({g[col].length})</span></Title>
          {g[col].map((card) => {
            const over = isOverdue(card, today);
            return (
              <Card key={`${card.sid}-${card.tnum}`}
                onClick={() => onOpen(card.sid, card.tnum)}
                className={`cursor-pointer p-3 ${over ? 'ring-2 ring-red-500' : ''}`}>
                <div className="flex justify-between text-sm font-medium">
                  <span>#{card.tnum}</span><span className="tabular-nums">{fmtMxn.format(card.total)}</span>
                </div>
                <Text className="text-xs">{card.anum_name}</Text>
                <Text className="text-xs">{card.vehicle || '—'}</Text>
                <div className="flex justify-between mt-1">
                  <Text className="text-xs">{card.tech}</Text>
                  {card.promise_date && <Badge color={over ? 'red' : 'gray'}>{over ? c.overdue : card.promise_date}</Badge>}
                </div>
              </Card>
            );
          })}
        </div>
      ))}
    </div>
  );
}
